import {
    CompetitorAnalysisComparison,
    CompetitorAnalysisResponse,
} from '../types/competitors';
import api from '../../../services/api';
import endpoints, {
    assistantEndpoints,
} from '../../../utils/commonVariables/endpoints';
import { base64ToArrayBuffer } from '../../../utils/encodeBase64';
import { validateCompetitorAnalysis } from './validateCompetitorAnalysis';

/**
 * Retrieves a finished competitor analysis file from the server.
 * @param idFile Identifier of the stored result file.
 * @returns The comparison stored in the file.
 * @throws If the server rejects the request or the file has an unexpected format.
 */
export const getCompetitorAnalysisFile = async (
    idFile: number
): Promise<CompetitorAnalysisComparison> => {
    const { data } = await api.post(endpoints.downloadScrapingFile, {
        d: 'File',
        Id: idFile.toString(),
    });
    if (data?.StatusCode !== 200 || !data?.Result?.Stream) {
        throw new Error('Could not retrieve the analysis file.');
    }
    const reportText = new TextDecoder('utf-8').decode(
        new Uint8Array(base64ToArrayBuffer(data.Result.Stream))
    );
    return validateCompetitorAnalysis(JSON.parse(reportText));
};

/**
 * Enqueues an asynchronous competitor analysis.
 * @param idHotel Numeric own hotel identifier.
 * @param competitor Competitor name, location or search query.
 * @param promptKey Scraping prompt configuration key.
 * @param idSupplierHotel Numeric supplier hotel identifier.
 * @returns The enqueue response; the result is delivered later as a notification.
 */
export const analyzeCompetitor = async (
    idHotel: number,
    competitor: string,
    promptKey: string,
    idSupplierHotel: number
): Promise<CompetitorAnalysisResponse> => {
    const response = await api.post<CompetitorAnalysisResponse>(
        assistantEndpoints.hotelCompetitorAnalysis,
        {
            idHotel: idHotel,
            idSupplierHotel,
            competitorsScrapingConfiguration: {
                useDefaultScrapingUrl: false,
                promptKey: promptKey,
                competitorInfo: competitor,
            },
        }
    );
    return response.data;
};
